import React, { useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { importStockRegisterApi } from "../../../../services/inventory";
import { appApi, useGetIssuedToOptionsQuery, useGetSkusQuery } from "../../../../store/appApi";
import { parseRegisterWorkbook, partyBase, partyKey } from "./stockRegisterParser";
import measurementData from "./measurement.json";

const CHUNK = 500;
const fmt = (n) =>
  n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const itemKey = (n) => String(n || "").trim().replace(/\s+/g, " ").toLowerCase();

// Group raw party spellings: same letters ignoring case/spaces/dots auto-merge
// ("K B K" = "KBK"); names that only differ by STORE / TRADERS / CO… are
// suggested, never merged without the user's choice.
const buildGroups = (rawCounts, existingNames) => {
  const byKey = new Map();
  rawCounts.forEach((count, raw) => {
    const k = partyKey(raw);
    if (!k) return;
    if (!byKey.has(k)) byKey.set(k, { key: k, spellings: new Map() });
    byKey.get(k).spellings.set(raw, (byKey.get(k).spellings.get(raw) || 0) + count);
  });
  const existingByKey = new Map(existingNames.map((n) => [partyKey(n), n]));
  const groups = [...byKey.values()].map((g) => {
    const spellings = [...g.spellings.entries()].sort((a, b) => b[1] - a[1]);
    const existing = existingByKey.get(g.key);
    return {
      key: g.key,
      name: existing || spellings[0][0],
      inDb: Boolean(existing),
      spellings,
      count: spellings.reduce((n, [, c]) => n + c, 0),
    };
  });
  groups.forEach((g) => {
    const base = partyBase(g.name);
    g.suggest = groups.filter((o) => o.key !== g.key && partyBase(o.name) === base).map((o) => o.name);
  });
  return groups.sort((a, b) => a.name.localeCompare(b.name));
};

const StockRegisterImport = ({ onDone }) => {
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const { data: skuData } = useGetSkusQuery();
  const { data: optData } = useGetIssuedToOptionsQuery();
  const skuNames = useMemo(
    () => (Array.isArray(skuData?.skus) ? skuData.skus : []).map((s) => s.skuName),
    [skuData],
  );
  const existingSkus = useMemo(() => new Set(skuNames.map(itemKey)), [skuNames]);
  const options = Array.isArray(optData?.options) ? optData.options : [];

  const [reading, setReading] = useState(false);
  const [sheets, setSheets] = useState(null);
  const [merge, setMerge] = useState({ vendor: {}, contractor: {} }); // group key -> target name
  const [preview, setPreview] = useState(null);
  const [stale, setStale] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [tab, setTab] = useState("issues");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);

  const readFile = async (file) => {
    try {
      setReading(true);
      setResult(null);
      setSheets(null);
      setPreview(null);
      setMerge({ vendor: {}, contractor: {} });
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());
      setSheets(
        parseRegisterWorkbook(wb.worksheets, todayKey()).map((s) => ({
          ...s,
          // sheets with an item on every row may overlap the item sheets → opt-in
          include: s.status === "ok" && !s.perRowItem,
        })),
      );
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not read file", text: err?.message || "Use an .xlsx file." });
    } finally {
      setReading(false);
    }
  };

  const patch = (idx, p) => {
    setSheets((all) => all.map((s, i) => (i === idx ? { ...s, ...p } : s)));
    setStale(true);
  };
  const chosen = (sheets || []).filter((s) => s.include && s.status === "ok");
  const totalRows = chosen.reduce((n, s) => n + s.rows.length, 0);

  // ── vendor / contractor spelling groups (from the chosen sheets) ───────────
  const groups = useMemo(() => {
    const vendors = new Map();
    const contractors = new Map();
    chosen.forEach((s) =>
      s.rows.forEach((r) => {
        const raw = String(r.party || "").trim();
        if (!raw || /^[-–—.]+$/.test(raw)) return;
        const m = r.entryType === "add" ? vendors : contractors;
        m.set(raw, (m.get(raw) || 0) + 1);
      }),
    );
    return {
      vendor: buildGroups(vendors, options.filter((o) => o.type === "supplier").map((o) => o.name)),
      contractor: buildGroups(contractors, options.filter((o) => o.type === "contractor").map((o) => o.name)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets, options]);

  // raw spelling -> final name, after the user's merge choices
  const buildAliases = () => {
    const out = { vendor: {}, contractor: {} };
    ["vendor", "contractor"].forEach((kind) => {
      const byName = new Map(groups[kind].map((g) => [g.name, g]));
      groups[kind].forEach((g) => {
        let target = merge[kind][g.key] && byName.has(merge[kind][g.key]) ? merge[kind][g.key] : g.name;
        // follow one more hop if the target was itself merged elsewhere
        const t2 = byName.get(target);
        if (t2 && merge[kind][t2.key] && byName.has(merge[kind][t2.key])) target = merge[kind][t2.key];
        g.spellings.forEach(([raw]) => {
          out[kind][partyKey(raw)] = target;
        });
      });
    });
    return out;
  };

  const payloadRows = () =>
    chosen.flatMap((s) =>
      s.rows.map((r) => ({ ...r, itemName: s.perRowItem ? r.itemName : s.itemName, unit: s.unit })),
    );

  const runPreview = async () => {
    try {
      setPreviewing(true);
      setResult(null);
      const { data } = await importStockRegisterApi({ rows: payloadRows(), aliases: buildAliases(), dryRun: true });
      setPreview(data);
      setStale(false);
      setTab("issues");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Preview failed",
        text: err?.response?.data?.message || err?.message || "Could not build the preview.",
      });
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    const ok = await Swal.fire({
      icon: "question",
      title: "Save to the database?",
      html: `<b>${preview.summary.toSave}</b> entries will be saved.<br/>New: <b>${preview.items.filter((i) => i.isNew).length}</b> item(s), <b>${preview.vendors.filter((i) => i.isNew).length}</b> vendor(s), <b>${preview.contractors.filter((i) => i.isNew).length}</b> contractor(s).`,
      showCancelButton: true,
      confirmButtonText: "Save",
    });
    if (!ok.isConfirmed) return;

    const aliases = buildAliases();
    const total = { created: 0, duplicates: 0, failed: 0, newItems: 0, newVendors: 0, newContractors: 0, errors: [], negative: [], sheetsDone: 0, sheetsFailed: [] };
    setBusy(true);
    setResult(null);
    for (let i = 0; i < chosen.length; i += 1) {
      const s = chosen[i];
      const rows = s.rows.map((r) => ({ ...r, itemName: s.perRowItem ? r.itemName : s.itemName, unit: s.unit }));
      try {
        for (let at = 0; at < rows.length; at += CHUNK) {
          setProgress(`Saving “${s.name}” (${i + 1}/${chosen.length}) — ${Math.min(at + CHUNK, rows.length)}/${rows.length}`);
          const { data } = await importStockRegisterApi({ rows: rows.slice(at, at + CHUNK), aliases });
          total.created += data.created || 0;
          total.duplicates += data.duplicates || 0;
          total.failed += data.failed || 0;
          total.newItems += data.newItems || 0;
          total.newVendors += data.newVendors || 0;
          total.newContractors += data.newContractors || 0;
          total.errors.push(...(data.errors || []));
          (data.negative || []).forEach((n) => {
            if (!total.negative.some((x) => x.skuName === n.skuName)) total.negative.push(n);
          });
        }
        total.sheetsDone += 1;
      } catch (err) {
        total.sheetsFailed.push({ name: s.name, message: err?.response?.data?.message || err?.message || "Failed" });
        break;
      }
    }
    setBusy(false);
    setProgress("");
    setResult(total);
    setPreview(null);
    dispatch(appApi.util.invalidateTags(["StoreTransactions", "SKUs", "Contractors", "Suppliers"]));
    if (total.created > 0 && onDone) onDone(total);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── discrepancies (parsed locally + returned by the server) ────────────────
  const issues = useMemo(() => {
    if (!preview) return [];
    const list = [];
    chosen.forEach((s) => {
      if (s.sheetClosing !== null && Math.abs(s.sheetClosing - s.computedClosing) > 0.01)
        list.push({ kind: "Closing differs", where: s.name, detail: `Import gives ${fmt(s.computedClosing)}, the sheet's own last balance(s) add up to ${fmt(s.sheetClosing)}.` });
      s.skipped.forEach((k) => list.push({ kind: "Row skipped", where: `${s.name} · row ${k.row}`, detail: k.why }));
      if (s.datesFilled > 0)
        list.push({ kind: "Blank dates filled", where: s.name, detail: `${s.datesFilled} row(s) had no date and took the date from the row above.` });
    });
    preview.negative.forEach((n) =>
      list.push({ kind: "Goes below zero", where: n.skuName, detail: `Lowest balance ${fmt(n.lowest)}${n.on ? ` on ${n.on}` : ""} (issued before it was received in the register).` }),
    );
    preview.errors.forEach((e) => list.push({ kind: "Row rejected", where: `row ${e.row}`, detail: e.message }));
    if (preview.summary.alreadyImported)
      list.push({ kind: "Already imported", where: "—", detail: `${preview.summary.alreadyImported} row(s) were saved by an earlier upload and will be skipped.` });
    return list;
  }, [preview, sheets]); // eslint-disable-line react-hooks/exhaustive-deps

  const entryRows = useMemo(() => {
    if (!preview) return [];
    const q = filter.trim().toLowerCase();
    return q
      ? preview.entries.filter((e) =>
          [e.item, e.party, e.billNo, e.truckNo, e.sheet, e.date].some((v) => String(v || "").toLowerCase().includes(q)),
        )
      : preview.entries;
  }, [preview, filter]);

  const downloadPreview = async () => {
    const wb = new ExcelJS.Workbook();
    const add = (name, cols, rows) => {
      const ws = wb.addWorksheet(name);
      ws.columns = cols.map(([header, key, width]) => ({ header, key, width }));
      ws.getRow(1).font = { bold: true };
      rows.forEach((r) => ws.addRow(r));
    };
    add("Discrepancies", [["Type", "kind", 22], ["Where", "where", 34], ["Detail", "detail", 90]], issues);
    add(
      "Entries",
      [["Date", "date", 12], ["Item", "item", 30], ["Type", "type", 9], ["Qty", "qty", 10], ["Vendor / Contractor", "party", 26], ["Bill / V. No", "billNo", 14], ["Truck No", "truckNo", 14], ["Open", "prevBalance", 10], ["Balance", "balance", 10], ["Location", "location", 16], ["Remarks", "remarks", 22], ["Sheet", "sheet", 22], ["Row", "row", 7]],
      preview.entries,
    );
    add("Items", [["Item", "name", 34], ["Unit", "unit", 10], ["New?", "isNew", 8], ["Entries", "entries", 9]], preview.items.map((i) => ({ ...i, isNew: i.isNew ? "new" : "" })));
    add("Vendors", [["Vendor", "name", 34], ["New?", "isNew", 8], ["Entries", "entries", 9]], preview.vendors.map((i) => ({ ...i, isNew: i.isNew ? "new" : "" })));
    add("Contractors", [["Contractor", "name", 34], ["New?", "isNew", 8], ["Entries", "entries", 9]], preview.contractors.map((i) => ({ ...i, isNew: i.isNew ? "new" : "" })));
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "stock-register-import-preview.xlsx",
    );
  };

  const mergeTable = (kind, label) => {
    const list = groups[kind];
    const names = list.map((g) => g.name);
    return (
      <div className="table-responsive" style={{ maxHeight: 360 }}>
        <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              <th>{label}</th>
              <th>Spellings in the file</th>
              <th className="text-end">Entries</th>
              <th>In database?</th>
              <th style={{ minWidth: 220 }}>Merge into</th>
            </tr>
          </thead>
          <tbody>
            {list.map((g) => {
              const pv = (preview?.[kind === "vendor" ? "vendors" : "contractors"] || []).find((x) => partyKey(x.name) === g.key);
              return (
                <tr key={g.key}>
                  <td className="fw-semibold">{g.name}</td>
                  <td>{g.spellings.length > 1 ? g.spellings.map(([r, c]) => `${r} (${c})`).join(", ") : <span className="text-muted">—</span>}</td>
                  <td className="text-end">{g.count}</td>
                  <td>{g.inDb || (pv && !pv.isNew) ? <span className="badge badge-success">existing</span> : <span className="badge badge-info">new</span>}</td>
                  <td>
                    <select
                      className="form-control form-control-sm"
                      value={merge[kind][g.key] || ""}
                      disabled={busy}
                      onChange={(e) => {
                        setMerge((m) => ({ ...m, [kind]: { ...m[kind], [g.key]: e.target.value } }));
                        setStale(true);
                      }}
                    >
                      <option value="">Keep separate</option>
                      {g.suggest.length > 0 && (
                        <optgroup label="Suggested (similar name)">
                          {g.suggest.map((n) => (
                            <option key={`s-${n}`} value={n}>
                              {n}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="All names">
                        {names
                          .filter((n) => n !== g.name && !g.suggest.includes(n))
                          .map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                      </optgroup>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const tabs = [
    ["issues", `Discrepancies (${issues.length})`],
    ["items", `Items (${preview?.items.length ?? 0})`],
    ["vendors", `Vendors (${groups.vendor.length})`],
    ["contractors", `Contractors (${groups.contractor.length})`],
    ["entries", `Entries (${preview?.entries.length ?? 0})`],
  ];

  return (
    <div className="card-body">
      <p className="text-muted small mb-3">
        Upload your stock register workbook (one sheet per item: DATE, VENDOR NAME, BILL NO / V. NO, OPEN QTY, RECEIPTS
        QTY, CONTRACTOR NAME, ISSUED QTY, BALANCE QTY, TRUCK NO, LOCATION, REMARKS). Nothing is saved until you have
        reviewed the preview and confirmed. Missing items, vendors and contractors are created together with the entries.
      </p>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        className="form-control form-control-sm mb-3"
        style={{ maxWidth: 360 }}
        disabled={reading || busy}
        onChange={(e) => e.target.files?.[0] && readFile(e.target.files[0])}
      />
      {reading && <div className="text-muted small">Reading file…</div>}

      {sheets && (
        <>
          <h6 className="mb-2">1. Sheets</h6>
          <div className="table-responsive" style={{ maxHeight: 300 }}>
            <table className="table table-sm table-bordered mb-2" style={{ fontSize: 12 }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th />
                  <th>Sheet</th>
                  <th style={{ minWidth: 190 }}>Item name</th>
                  <th>Unit</th>
                  <th className="text-end">Receipts</th>
                  <th className="text-end">Issues</th>
                  <th className="text-end" title="Receipts − issues from this import / sum of the sheet's own last balance(s)">
                    Closing (import / sheet)
                  </th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {sheets.map((s, idx) => {
                  if (s.status !== "ok") {
                    return (
                      <tr key={s.name} className="text-muted">
                        <td />
                        <td>{s.name}</td>
                        <td colSpan={6}>Skipped — {s.reason}</td>
                      </tr>
                    );
                  }
                  const mismatch = s.sheetClosing !== null && Math.abs(s.sheetClosing - s.computedClosing) > 0.01;
                  const isNew = !s.perRowItem && !existingSkus.has(itemKey(s.itemName));
                  return (
                    <tr key={s.name} className={s.include ? "" : "text-muted"}>
                      <td>
                        <input type="checkbox" checked={s.include} disabled={busy} onChange={(e) => patch(idx, { include: e.target.checked })} />
                      </td>
                      <td>{s.name}</td>
                      <td>
                        {s.perRowItem ? (
                          <em>item on every row</em>
                        ) : (
                          <>
                            <input
                              className="form-control form-control-sm"
                              list="register-existing-skus"
                              value={s.itemName}
                              disabled={busy}
                              onChange={(e) => patch(idx, { itemName: e.target.value })}
                            />
                            <span className={`badge ${isNew ? "badge-info" : "badge-success"} mt-1`}>{isNew ? "new item" : "existing item"}</span>
                          </>
                        )}
                      </td>
                      <td>
                        <select className="form-control form-control-sm" value={s.unit} disabled={busy || s.perRowItem} onChange={(e) => patch(idx, { unit: e.target.value })}>
                          {[...new Set([s.unit, ...measurementData.measurements])].map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-end">
                        {s.receipts} · {fmt(s.receiptQty)}
                      </td>
                      <td className="text-end">
                        {s.issues} · {fmt(s.issueQty)}
                      </td>
                      <td className={`text-end ${mismatch ? "text-warning fw-semibold" : ""}`}>
                        {fmt(s.computedClosing)} / {fmt(s.sheetClosing)}
                      </td>
                      <td>
                        {s.perRowItem && <div>Review — may overlap the item sheets (off by default).</div>}
                        {s.skipped.length > 0 && <div>{s.skipped.length} row(s) skipped</div>}
                        {s.datesFilled > 0 && <div>{s.datesFilled} blank date(s) filled from the row above</div>}
                        {mismatch && <div className="text-warning">closing differs from the sheet</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <datalist id="register-existing-skus">
              {skuNames.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>

          <h6 className="mt-3 mb-2">2. Vendors &amp; contractors — merge spellings</h6>
          <p className="text-muted small mb-2">
            Names that differ only by case, spaces or dots (K B K = KBK) are already one record. For names that look
            alike (CHEEMA / CHEEMA STORE) choose “Merge into” if they are the same.
          </p>
          <div className="row">
            <div className="col-lg-6 mb-2">{mergeTable("vendor", "Vendor")}</div>
            <div className="col-lg-6 mb-2">{mergeTable("contractor", "Contractor")}</div>
          </div>

          <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy || previewing || !totalRows} onClick={runPreview}>
              {previewing ? "Building preview…" : preview && !stale ? "Refresh preview" : `3. Preview ${chosen.length} sheet(s) · ${totalRows} entries`}
            </button>
            {stale && preview && <span className="small text-warning">You changed something — refresh the preview before saving.</span>}
          </div>
        </>
      )}

      {preview && (
        <div className="mt-3 border rounded p-2">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <strong>Preview — nothing saved yet</strong>
            <span className="small text-muted">
              {preview.summary.toSave} entries to save · {preview.items.filter((i) => i.isNew).length} new item(s) ·{" "}
              {preview.vendors.filter((i) => i.isNew).length} new vendor(s) · {preview.contractors.filter((i) => i.isNew).length} new contractor(s)
              {preview.summary.alreadyImported ? ` · ${preview.summary.alreadyImported} already imported` : ""}
            </span>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={downloadPreview}>
              <i className="fa fa-download me-1" />
              Download preview (Excel)
            </button>
          </div>
          <ul className="nav nav-tabs mb-2">
            {tabs.map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button type="button" className={`nav-link py-1 ${tab === key ? "active" : ""}`} onClick={() => setTab(key)}>
                  {label}
                </button>
              </li>
            ))}
          </ul>

          {tab === "issues" && (
            <div className="table-responsive" style={{ maxHeight: 320 }}>
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>Type</th>
                    <th style={{ width: 240 }}>Where</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-success py-3">
                        No discrepancies found.
                      </td>
                    </tr>
                  )}
                  {issues.map((i, n) => (
                    <tr key={n}>
                      <td>{i.kind}</td>
                      <td>{i.where}</td>
                      <td>{i.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(tab === "items" || tab === "vendors" || tab === "contractors") && (
            <div className="table-responsive" style={{ maxHeight: 320 }}>
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    {tab === "items" && <th>Unit</th>}
                    <th className="text-end">Entries</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview[tab].map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      {tab === "items" && <td>{r.unit}</td>}
                      <td className="text-end">{r.entries}</td>
                      <td>{r.isNew ? <span className="badge badge-info">will be created</span> : <span className="badge badge-success">existing</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "entries" && (
            <>
              <input className="form-control form-control-sm mb-2" style={{ maxWidth: 320 }} placeholder="Search item, vendor, bill no, truck, sheet…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <div className="table-responsive" style={{ maxHeight: 320 }}>
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                  <thead style={{ position: "sticky", top: 0, background: "#fff" }}>
                    <tr>
                      <th>Date</th>
                      <th>Item</th>
                      <th>Type</th>
                      <th className="text-end">Open</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Balance</th>
                      <th>Vendor / Contractor</th>
                      <th>Bill / V. No</th>
                      <th>Truck</th>
                      <th>Location / Remarks</th>
                      <th>Sheet · row</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryRows.slice(0, 300).map((e, i) => (
                      <tr key={i} className={e.balance < 0 ? "table-danger" : ""}>
                        <td>{e.date}</td>
                        <td>{e.item}</td>
                        <td>{e.type === "add" ? "Received" : "Issued"}</td>
                        <td className="text-end">{fmt(e.prevBalance)}</td>
                        <td className={`text-end ${e.type === "add" ? "text-success" : "text-danger"}`}>
                          {e.type === "add" ? "+" : "-"}
                          {fmt(e.qty)}
                        </td>
                        <td className="text-end fw-semibold">{fmt(e.balance)}</td>
                        <td>{e.party || "—"}</td>
                        <td>{e.billNo || "—"}</td>
                        <td>{e.truckNo || "—"}</td>
                        <td>{[e.location, e.remarks].filter(Boolean).join(" · ") || "—"}</td>
                        <td>
                          {e.sheet} · {e.row || "opening"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="small text-muted mt-1">
                Showing {Math.min(300, entryRows.length)} of {entryRows.length} — the full list is in the Excel download.
              </div>
            </>
          )}

          <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || stale || !preview.summary.toSave} onClick={runImport}>
              {busy ? "Saving…" : `Save ${preview.summary.toSave} entries to the database`}
            </button>
            {progress && <span className="small text-muted">{progress}</span>}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-3">
          <div className={`alert ${result.failed || result.sheetsFailed.length ? "alert-warning" : "alert-success"} py-2 mb-2`}>
            {result.created} entries saved across {result.sheetsDone} sheet(s)
            {result.duplicates ? `, ${result.duplicates} already imported (skipped)` : ""}
            {result.failed ? `, ${result.failed} rows rejected` : ""}. Created {result.newItems} item(s), {result.newVendors} vendor(s), {result.newContractors} contractor(s).
          </div>
          {result.sheetsFailed.map((f) => (
            <div key={f.name} className="alert alert-danger py-2 mb-2">
              Stopped at “{f.name}”: {f.message}. Sheets already saved stay saved — fix the problem and upload again; they will be skipped.
            </div>
          ))}
          {result.errors.length > 0 && (
            <div className="table-responsive" style={{ maxHeight: 200 }}>
              <table className="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Row</th>
                    <th>Problem</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.slice(0, 100).map((e, i) => (
                    <tr key={i}>
                      <td>{e.row}</td>
                      <td>{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockRegisterImport;
